import { TOrganizationDetailDto } from "@/api/services/admin/organization/dtos/get-organization-detail.dto";
import { useEnum } from "@/api/services/global/enum";
import { IconOpenInNew } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { DetailItem } from "@/components/elements/DetailItem";
import { Modal } from "@/components/elements/Modal/Modal";
import { SectionCard } from "@/components/elements/SectionCard";
import { EMPTY_STR } from "@/constants/string";
import { AppRoutes } from "@/routes/config";
import { getDynamicRoute } from "@/utils/url";
import { findLabelByValue } from "@/utils/object";
import { Grid, Stack, Typography } from "@mui/material";
import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";

type Props = {
  organization: TOrganizationDetailDto;
};

const CRM_LINK_EMPTY_MESSAGE = "CRMリンクが設定されていません。編集からリンク先を設定するか、新規作成してください。";
const CRM_LINK_INVALID_MESSAGE = "CRMリンクが無効です。編集からリンク先を修正してください";

const isValidUrl = (value: string) => {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

export const OrganizationDetailBasicInfo: FC<Props> = ({ organization }) => {
  const navigate = useNavigate();
  const { data: organizationTypes } = useEnum("EOrganizationType");
  const { data: organizationStatuses } = useEnum("EOrganizationStatus");

  const [crmDialogMessage, setCrmDialogMessage] = useState<string | null>(null);

  const formatDate = (date: string | null | undefined) => {
    if (!date) return EMPTY_STR.DATE;
    return new Date(date).toLocaleDateString("ja-JP", { year: "numeric", month: "2-digit", day: "2-digit" });
  };

  const handleOpenCrm = () => {
    const link = organization.crmLink?.trim();
    if (!link) {
      setCrmDialogMessage(CRM_LINK_EMPTY_MESSAGE);
      return;
    }
    if (!isValidUrl(link)) {
      setCrmDialogMessage(CRM_LINK_INVALID_MESSAGE);
      return;
    }
    window.open(link, "_blank");
  };

  const handleCloseCrmDialog = () => setCrmDialogMessage(null);

  const handleNavigateToEdit = () => {
    setCrmDialogMessage(null);
    navigate(getDynamicRoute(AppRoutes.organizationEdit, { id: String(organization.id) }));
  };

  return (
    <SectionCard title="基本情報" maxWidth="100%">
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <DetailItem isInput label="組織ID" value={organization.externalId} />
        </Grid>
        <Grid item xs={6}>
          <DetailItem isInput label="ステータス" value={findLabelByValue(organizationStatuses, organization.status)} />
        </Grid>
        <Grid item xs={6}>
          <DetailItem isInput label="組織名" value={organization.name} />
        </Grid>
        <Grid item xs={6}>
          <Stack direction="row" gap="24px" alignItems="center" justifyContent="space-between">
            <DetailItem
              sx={{
                width: "100%",
              }}
              isInput
              label="機関種別"
              value={findLabelByValue(organizationTypes, organization.type)}
            />
            <Button size="sm" variant="outline" onClick={handleOpenCrm} sx={{ gap: 1, minWidth: "90px !important" }}>
              CRM{" "}
              <Stack width={20}>
                <IconOpenInNew color="currentColor" fontSize={20} />
              </Stack>
            </Button>
          </Stack>
        </Grid>
        <Grid item xs={2.76}>
          <DetailItem label="住所" value={[organization.provinceName, organization.municipalityName].filter(Boolean).join("")} />
        </Grid>
        <Grid item xs={2.76}>
          <DetailItem label="担当者" value={organization.personInCharge?.fullName ?? EMPTY_STR.TEXT} />
        </Grid>
        <Grid item xs={2.16}>
          <DetailItem label="登録日" value={formatDate(organization.createdAt)} />
        </Grid>
        <Grid item xs={2.16}>
          <DetailItem label="アーカイブ日" value={formatDate(organization.archivedAt)} />
        </Grid>
        <Grid item xs={2.16}>
          <DetailItem label="削除日" value={formatDate(organization.deletedAt)} />
        </Grid>
      </Grid>
      <Modal
        isOpen={crmDialogMessage !== null}
        title="CRMリンク"
        labelClose="キャンセル"
        labelOk="組織を編集"
        onClose={handleCloseCrmDialog}
        onOk={handleNavigateToEdit}
      >
        <Stack sx={{ mt: 1, mb: 3 }}>
          <Typography variant="cap12">{crmDialogMessage}</Typography>
        </Stack>
      </Modal>
    </SectionCard>
  );
};
